import { Container } from '@hg/stylizator/components'
import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { desktop } from '@hg/stylizator/utils'
import * as React from 'react'
import styled, { css, useTheme } from 'styled-components'

import BottomCTA from 'app/components/blog/BottomCTA'
import PaginateDots from 'app/components/blog/PaginateDots'
import PaginateLink from 'app/components/blog/PaginateLink'
import Post from 'app/components/blog/Post'
import PromoBannerNoImage from 'app/components/blog/PromoBanner/PromoBannerNoImage'
import PromoBannerWithImage from 'app/components/blog/PromoBanner/PromoBannerWithImage'
import RelatedPosts from 'app/components/blog/RelatedPosts'
import SearchAndCategories from 'app/components/blog/SearchAndCategories'
import ClientOnly from 'app/components/ClientOnly'
import { DOTS, usePagination } from 'app/hooks/use-pagination'
import { usePromoBannerData } from 'app/hooks/use-promo-banner-data'
import { useTranslation } from 'app/hooks/use-translation'
import Chevron from 'app/icons/Chevron'
import SingleColumnLayout from 'app/layouts/SingleColumnLayout'
import { TPageData } from 'app/service/api/theme'

interface IProps {
  pageData: TPageData<'category'>
}

const PAGE_REGEXP = /page\/([0-9]+)\//

const CategoryPage: React.FC<IProps> = ({ pageData }) => {
  const theme = useTheme()
  const t = useTranslation()
  const { isDesktop } = useSizes()
  const promoBannerData = usePromoBannerData()

  const paginationRange = usePagination({
    currentPage: pageData.current_page,
    totalPageCount: pageData.page_count,
    createUrlByPage: (page) => {
      const newPage = page === 1 ? '' : `page/${page}/`
      const url = new URL(pageData.category.url)
      url.pathname = url.pathname.replace(PAGE_REGEXP, '')
      url.pathname = `${url.pathname}${newPage}`
      return url.toString()
    },
  })

  const showPagination = pageData.page_count > 1

  const firstPartPosts = pageData.posts.slice(0, pageData.posts.length / 2)
  const secondPartPosts = pageData.posts.slice(pageData.posts.length / 2)

  return (
    <SingleColumnLayout>
      <Background>
        <Container
          margins={{
            mobile: 0,
            tablet: 0,
            desktop: 0,
            largeDesktop: 0,
          }}
          style={{ padding: '0 16px', boxSizing: 'border-box' }}
        >
          <BlogLayout>
            <SearchAndCategories categories={pageData.categories} currentCategory={pageData.category} />

            <MainAndAside>
              <Main>
                <Posts>
                  <Text type="heading_medium" useCompact>
                    <h1>{pageData.category.name}</h1>
                  </Text>

                  <ClientOnly>
                    {firstPartPosts.map((post) => (
                      <Post
                        key={post.permalink}
                        variant={isDesktop ? 'small' : 'xsmall'}
                        post={post}
                        titleHeading="h2"
                      />
                    ))}
                    {isDesktop ? null : (
                      <PromoBannerSpacing>
                        <PromoBannerWithImage {...promoBannerData} variation="small" />
                      </PromoBannerSpacing>
                    )}
                    {secondPartPosts.map((post) => (
                      <Post
                        key={post.permalink}
                        variant={isDesktop ? 'small' : 'xsmall'}
                        post={post}
                        titleHeading="h2"
                      />
                    ))}
                  </ClientOnly>
                </Posts>

                {showPagination && (
                  <Pagination>
                    <ArrowButton disabled={pageData.prev_page === null} href={pageData.prev_page ?? ''}>
                      <Chevron color={theme.color.blue[60]} width={26} height={26} />
                    </ArrowButton>

                    {paginationRange.map(({ page, url }, index) =>
                      page === DOTS ? (
                        <PaginateDots key={`dots_${index}`} />
                      ) : (
                        <PaginateLink key={url} href={url} isActive={page === pageData.current_page}>
                          {page}
                        </PaginateLink>
                      ),
                    )}

                    <ArrowButton disabled={pageData.next_page === null} href={pageData.next_page ?? ''}>
                      <Chevron
                        color={theme.color.blue[60]}
                        width={26}
                        height={26}
                        style={{ transform: 'rotate(180deg)' }}
                      />
                    </ArrowButton>
                  </Pagination>
                )}
              </Main>

              <Sidebar>
                <PromoBannerNoImage {...promoBannerData} variation="small" />
              </Sidebar>
            </MainAndAside>
          </BlogLayout>
        </Container>
      </Background>

      <RelatedPosts posts={pageData.recent} title={t('recent_articles')} />

      <TvSofaImage src="/assets/illustrations/tv-sofa.svg" />
      <BottomCTA />
    </SingleColumnLayout>
  )
}

const Background = styled.div`
  background: ${(props) => props.theme.color.blue[5]};
`

const BlogLayout = styled.div`
  padding: 40px 0;

  @media ${desktop} {
    padding: 56px 0 112px;
  }
`

const MainAndAside = styled.div`
  display: grid;
  margin-top: 40px;
  grid-template-columns: 1fr;
  grid-template-areas:
    'main'
    'sidebar';

  @media ${desktop} {
    margin: 56px 0 64px;
    column-gap: 48px;
    grid-template-columns: 1fr 300px;
    grid-template-areas: 'main sidebar';
  }
`

const Main = styled.main`
  grid-area: main;
`

const Sidebar = styled.aside`
  display: none;
  grid-area: sidebar;

  @media ${desktop} {
    position: sticky;
    top: 80px;
    align-self: flex-start;
    display: block;
    flex: 1;
    width: 100%;
    max-width: 300px;
  }
`

const Posts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${desktop} {
    gap: 48px;
  }
`

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  @media ${desktop} {
    margin-top: 72px;
  }
`

const TvSofaImage = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  height: 48vw;
  padding-top: 96px;
  background-color: ${(props) => props.theme.color.blue[5]};

  @media ${desktop} {
    height: auto;
  }
`

const ArrowButton = styled.a<{ disabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 32px;
  border: 1px solid ${(props) => props.theme.color.blue[60]};
  border-radius: 8px;

  :focus {
    box-shadow: 0px 0px 0px 4px ${(props) => props.theme.color.blue[30]};
  }

  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.2;
    `}
`

const PromoBannerSpacing = styled.div`
  margin: 0 -16px;
`

export default CategoryPage
